import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  List,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {
  goalDetails,
  goalSummary,
  introText,
  requirementLines,
  requirementSummary,
  reviewChecks,
} from '../../model/review';
import { useSetup } from '../../SetupContext';

export function ReviewStep() {
  const { session, config, update, values, issues, readOnly, goTo } =
    useSetup();
  const requirements = requirementLines(session, config);
  const enforced = requirements.filter(r => r.enforced);
  const notEnforced = requirements.filter(r => !r.enforced);
  const checks = reviewChecks(session, config, issues);

  return (
    <Grid gutter="lg" align="flex-start">
      <Grid.Col span={{ base: 12, lg: 8 }}>
        <Card withBorder p={0} radius="lg">
          <Box px={28} pt={24} pb={18}>
            <Title order={2} c="medleyGreen.8">
              Review and confirm
            </Title>
            <Text c="dimmed" mt={6}>
              Check that this is what you want before matching starts.
            </Text>
          </Box>
          <Divider />
          <Paper bg="gray.0" withBorder radius="md" mx={28} mt="lg" p="lg">
            <Text fw={600} mb="xs">
              What this means
            </Text>
            <Text size="sm" lh={1.55} mb="md">
              {introText(session, config)}
            </Text>
            <Text fw={600} size="sm">
              Group requirements
            </Text>
            <Text size="sm" lh={1.55} mb="md">
              {requirementSummary(requirements)}
            </Text>
            <Text fw={600} size="sm">
              Matching goals
            </Text>
            <Text size="sm" lh={1.55}>
              {goalSummary(session, config, values)}
            </Text>
          </Paper>

          <Box px={28} pt="xl">
            <Title order={4} mb="sm">
              Group requirement details
            </Title>
            <Text fw={600} size="sm" mb={6}>
              Enforced
            </Text>
            <List size="sm" spacing={6}>
              {enforced.map(r => (
                <List.Item key={r.name}>{r.text}</List.Item>
              ))}
            </List>
            {notEnforced.length > 0 && (
              <>
                <Text fw={600} size="sm" mt="md" mb={6}>
                  Not enforced
                </Text>
                <List size="sm" spacing={6}>
                  {notEnforced.map(r => (
                    <List.Item key={r.name}>{r.text}</List.Item>
                  ))}
                </List>
              </>
            )}
          </Box>

          <Box px={28} pt="xl">
            <Title order={4} mb="sm">
              Matching goal details
            </Title>
            {config.goals.length === 0 && (
              <Text size="sm" c="dimmed">
                No matching goals set.
              </Text>
            )}
            <Stack gap="md">
              {goalDetails(session, config, values).map(g => (
                <div key={g.column}>
                  <Group gap="sm">
                    <Text fw={600}>{g.name}</Text>
                    {!g.saved && (
                      <Badge color="dangerRed" variant="filled">
                        Not saved
                      </Badge>
                    )}
                  </Group>
                  <List size="sm" spacing={6} mt={6}>
                    {g.lines.map(l => (
                      <List.Item key={l}>{l}</List.Item>
                    ))}
                  </List>
                </div>
              ))}
            </Stack>
          </Box>

          <Group gap="sm" px={28} pt="lg" pb={28}>
            <Button variant="default" onClick={() => goTo({ step: 'data' })}>
              Edit data
            </Button>
            <Button variant="default" onClick={() => goTo({ step: 'goals' })}>
              Edit group requirements and matching goals
            </Button>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="lg">
          <Card withBorder radius="lg" p="lg">
            <Title order={3} c="medleyGreen.8">
              Name this request
            </Title>
            <Text size="sm" c="dimmed" mt={4} mb="md">
              So you can find it later.
            </Text>
            <TextInput
              label="Request name"
              value={config.request.name}
              readOnly={readOnly}
              onChange={e =>
                update({ type: 'setRequest', name: e.currentTarget.value })
              }
            />
            <Textarea
              label="Note (optional)"
              mt="md"
              autosize
              minRows={3}
              placeholder="Anything to remember about this request, for example first run"
              value={config.request.note}
              readOnly={readOnly}
              onChange={e =>
                update({ type: 'setRequest', note: e.currentTarget.value })
              }
            />
          </Card>

          <Card withBorder radius="lg" p="lg">
            <Title order={3} c="medleyGreen.8">
              Checks
            </Title>
            <Stack gap="sm" mt="md">
              {checks.map(c => (
                <Group key={c.text} gap={10} wrap="nowrap" align="flex-start">
                  <Box
                    w={10}
                    h={10}
                    mt={6}
                    bg={c.ok ? 'medleyGreen.6' : 'dangerRed.5'}
                    style={{ borderRadius: 5, flex: 'none' }}
                  />
                  <Text
                    size="sm"
                    fw={c.ok ? 400 : 600}
                    c={c.ok ? undefined : 'dangerRed.8'}
                    style={{ flex: 1 }}
                  >
                    {c.text}
                  </Text>
                  {c.issue && (
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={() => goTo(c.issue!.target)}
                    >
                      Fix
                    </Anchor>
                  )}
                </Group>
              ))}
            </Stack>
          </Card>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
